import React from 'react';
import styled, { TC } from '@benzinga/themetron';
import { URLString } from '@benzinga/session';
import { ChatNickname } from '@benzinga/chat-manager';
import { Avatar, Popover } from 'antd'; // photon
import { hsl, readableColor } from 'polished';
import { pipe, reduce, split } from 'ramda';

export function firstChar(str: string): string {
  if (str.match(/^[\uD800-\uDBFF]/)) {
    return str.slice(0, 2);
  }

  return str.slice(0, 1);
}

interface Props {
  colorSeed: string;
  iconUrl: URLString;
  nickname: ChatNickname;
  width: string;
}

export const ChatAvatar: React.FC<Props> = React.memo(({ colorSeed, iconUrl, nickname, width }) => {
  return (
    <AvatarContainer $width={width}>
      <Popover
        arrow={{ pointAtCenter: true }}
        content={popoverContent}
        placement="bottomLeft"
        title={<PopoverTitle>{nickname}</PopoverTitle>}
      >
        <Avatar
          shape="square"
          size="small"
          {...(iconUrl
            ? { src: iconUrl }
            : { style: { backgroundColor: avatarColor(colorSeed), color: readableColor(avatarColor(colorSeed)) } })}
        >
          {firstChar(nickname || '').toUpperCase()}
        </Avatar>
      </Popover>
    </AvatarContainer>
  );
});

interface AvatarContainerProps {
  $width: string;
}

export const AvatarContainer = styled(TC.Row)<AvatarContainerProps>`
  display: flex;
  flex-shrink: 0;
  padding-top: 2px;
  // warning: magic number
  width: ${props => props.$width};
`;

const PopoverTitle = styled.div`
  padding-bottom: 0.5em;
  padding-left: 1em;
  padding-right: 1em;
  padding-top: 0.5em;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const PopoverContent = styled.div`
  padding-bottom: 0.5em;
  padding-left: 1em;
  padding-right: 1em;
  padding-top: 0.5em;
`;

const popoverContent = <PopoverContent>Benzinga Pro user</PopoverContent>;

const avatarColorFactory =
  (randomSeed: number, saturation: number, lightness: number) =>
  (name = '') => {
    const hue = pipe(
      split(''),
      reduce((accumulator, character) => (accumulator * randomSeed + character.charCodeAt(0)) * randomSeed, 0),
      value => value % 360,
    )(name);

    return hsl(hue, saturation, lightness);
  };

export const avatarColor = avatarColorFactory(13.111, 0.75, 0.6);
